import { EDuration, EPaidProductType } from '../type'
import { getCurrentPrice } from '../helpers/getCurrentPrice'
import { getPaidProductType } from '../helpers/getProductType'

import type { $Enums } from '@prisma/client'
import type { EProductType, TProduct, TProductsList } from '../type'
import type { TUsePlanProductList } from './planProductList'
import type { TUseSubLog } from '@/contexts/SiteSpecificContext/hooks/subLog'
import type { TUseDuration } from './duration'
import type { TUsePayStep } from './payStep'


import { useState, useMemo, useCallback, useEffect } from 'react';
export interface IPackageGpt35ChangeProps {
  product: TProductsList[number]
  productType: EPaidProductType
  duration: EDuration
}

export interface IUseSelectPackageProps {
  onPackageCreditsChange?: (props: IPackageGpt35ChangeProps) => void
}

export interface IChangeProps {
  priceId: string
  type: EPaidProductType | EProductType
  nextItem?: TProduct
}

function getNextProduct({
  products,
  currentUpgradeWeight,
}: {
  products: TProduct[]
  currentUpgradeWeight: number
  appName: $Enums.SubLogAppName
  type: EPaidProductType | EProductType
}) {
  const index = products.findIndex(
    (item) => item.upgradeWeight === currentUpgradeWeight,
  )

  const lastIndex = products.length - 1
  const lastProduct = products[lastIndex]

  if (index === -1 || index === lastIndex) {
    return lastProduct
  }

  return products[index + 1]
}

export function getDefaultCreditsPackage({
  productInfo,
  appName,
}: {
  productInfo: TUsePlanProductList['allProductsList'][number]
  appName: $Enums.SubLogAppName
}) {
  const { type, creditsProducts: gpt35Products } = productInfo
  const defaultProduct = gpt35Products[0]

  return defaultProduct
}

export function useSelectPackage({
  onPackageCreditsChange,
  allProductsListMap,
  plan,
  appName,
  productInfo,
  durationInfo,
  isActive,
  payStepInfo,
  isAnonymousPayment,
}: IUseSelectPackageProps & {
  allProductsListMap: TUsePlanProductList['allProductsListMap']
  plan: TUseSubLog
  appName: $Enums.SubLogAppName
  productInfo: TUsePlanProductList['allProductsList'][number]
  durationInfo: TUseDuration
  isActive: boolean
  payStepInfo: TUsePayStep
  isAnonymousPayment?: boolean
}) {
  const { creditsPlan, creditsProductType, hasPlan } = plan

  const { creditsProducts = [], type, duration: productDuration } = productInfo

  const productsListMap = allProductsListMap[type]

  const { monthProductMap, yearProductMap } = productsListMap
  const monthCreditsProducts = monthProductMap.creditsProducts
  const yearCreditsProducts = yearProductMap.creditsProducts

  const defaultCreditsPackage = getDefaultCreditsPackage({
    productInfo,
    appName,
  })

  const [selectedCreditsPackage, setSelectedCreditsPackage] =
    useState<TProduct>(defaultCreditsPackage)

  const priceCredits = useMemo(() => {
    return getCurrentPrice({
      productPrice: selectedCreditsPackage,
    })
  }, [selectedCreditsPackage])

  const monthCreditsPackage = useMemo(() => {
    return monthCreditsProducts.find(
      (item) => item.usageCount === selectedCreditsPackage?.usageCount,
    )
  }, [monthCreditsProducts, selectedCreditsPackage?.usageCount])

  const priceMonthCreditsPackage = useMemo(() => {
    return getCurrentPrice({
      productPrice: monthCreditsPackage,
    })
  }, [monthCreditsPackage])

  const outletsText = useMemo(() => {
    const priceTotal = priceCredits.currencyPriceMonth
    const priceTotalMonth = priceMonthCreditsPackage.currencyPriceMonth
    const outlets = Math.round(
      ((priceTotalMonth - priceTotal) / priceTotalMonth) * 100,
    )

    return `${outlets}%`
  }, [
    priceCredits.currencyPriceMonth,
    priceMonthCreditsPackage.currencyPriceMonth,
  ])

  const onChangeCreditsPackage = useCallback(
    ({ priceId, type, nextItem }: IChangeProps) => {
      const product = creditsProducts.find((item) => item.priceId === priceId)

      if (!product) {
        // console.log(gpt3_5Products, 'gpt3_5Products')
        // console.log(selectedGpt3_5Package, 'selectedGpt3_5Package')
        // console.log(nextItem, 'nextItem')
        // console.log(yearGpt3_5Products, 'yearGpt3_5Products')
        // eslint-disable-next-line no-console
        console.warn('onChangeCreditsPackage: product not found')
        return
      }
      setSelectedCreditsPackage(product)
    },
    [creditsProducts],
  )

  // creditsProducts 回显
  useEffect(() => {
    if (!hasPlan || !creditsPlan) {
      setSelectedCreditsPackage(defaultCreditsPackage)
      return
    }

    const { productsSub } = creditsPlan
    const planDuration = productsSub.duration

    if (
      productDuration === EDuration.MONTHLY &&
      planDuration === EDuration.YEARLY
    ) {
      return
    }

    if (type === EPaidProductType.UNLIMITED) {
      return
    }

    const products =
      productDuration === EDuration.MONTHLY
        ? monthCreditsProducts
        : yearCreditsProducts

    const nextItem = getNextProduct({
      products,
      currentUpgradeWeight: productsSub.upgradeWeight,
      appName,
      type,
    })

    onChangeCreditsPackage({
      priceId: nextItem.priceId,
      type,
      nextItem,
    })
  }, [
    appName,
    defaultCreditsPackage,
    creditsProducts,
    creditsPlan,
    hasPlan,
    monthCreditsProducts,
    onChangeCreditsPackage,
    productDuration,
    type,
    yearCreditsProducts,
  ])

  //  onPackageChange 回调
  useEffect(() => {
    if (selectedCreditsPackage) {
      const productType = getPaidProductType({
        product: selectedCreditsPackage,
      })
      onPackageCreditsChange?.({
        product: selectedCreditsPackage,
        productType,
        duration: selectedCreditsPackage.duration,
      })
    }
  }, [selectedCreditsPackage])

  return {
    selectedCreditsPackage,
    outletsText,
    onChangeCreditsPackage,
  }
}
